<template>
  <div class="wd-relative">
    <div class="gallery-grid wd-grid wd-gap-x-3 wd-gap-y-2" :style="gridStyle">
      <button
        v-for="(cell, idx) in visibleCells"
        :key="`${cell.url}-${idx}`"
        type="button"
        class="gallery-cell wd-overflow-hidden wd-rounded"
        :class="cell.isHero ? 'gallery-cell--hero' : 'gallery-cell--thumb'"
        :style="{ aspectRatio: cell.aspectRatio }"
        @click="emit('open-lightbox', idx)"
      >
        <img :src="cell.url" :alt="alt" class="wd-w-full wd-h-full wd-object-cover" />
      </button>
    </div>

    <v-btn
      v-if="hasMoreThanVisible"
      variant="flat"
      color="white"
      :height="32"
      class="view-more-btn wd-absolute wd-bottom-3 wd-right-3 !wd-px-3"
      @click="emit('open-lightbox', 0)"
    >
      <span class="wd-flex wd-items-center wd-gap-1 wd-text-omd">
        <span class="wd-flex wd-w-5 wd-h-5 wd-items-center wd-justify-center">
          <funnow-svg-icon image-path="gallery.svg" max-width="16" height="16" />
        </span>
        {{ $t('browse_all_images') }}
      </span>
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FunnowSvgIcon from '@/components/icons/funnow-svg-icon.vue'

import { BRANCH_IMAGE_GALLERY } from '@/constants/branch'

import type { BranchGalleryImage } from '@/types/branch'

interface Props {
  images: BranchGalleryImage[]
  alt?: string
}

type Emits = {
  'open-lightbox': [index: number]
}

// At most four thumbnails are shown; a "browse all" button appears when
// there are more non-hero images than this.
const MAX_THUMBS = 4
const COL_GAP_PX = 12
const THUMB_MAX_WIDTH_PX = 256
// Hero / thumb cell sizes are pinned to the canonical "2 heroes + 2 thumb
// columns fill the row" layout, so they stay constant regardless of hero
// count. In column units a hero is 9 wide (9:5 ratio) and a thumb is 2.5
// wide (a square half the hero's 5-unit height), so the reference row is
// 9 + 9 + 2.5 + 2.5 = 23 units across 4 columns. The thumb formula
// encodes that: 28px = 3 × column gap(12) − row gap(8); 46 = 2 × 23 units.
// Capped at 256px. The hero is derived from the thumb to stay locked: it
// spans both rows, so its height is `2 thumbs + 8px row gap` and its
// width is that height × 9/5.
const THUMB_COLUMN_WIDTH = `min(calc(5 * (100% - 28px) / 46 - 4px), ${THUMB_MAX_WIDTH_PX}px)`
const HERO_COLUMN_WIDTH = `calc((2 * ${THUMB_COLUMN_WIDTH} + 8px) * 9 / 5)`

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const heroImages = computed(() => props.images.filter(image => image.isPrimary))
const thumbImages = computed(() => props.images.filter(image => !image.isPrimary))
const visibleThumbs = computed(() => thumbImages.value.slice(0, MAX_THUMBS))
const hasMoreThanVisible = computed(() => thumbImages.value.length > MAX_THUMBS)

// Heroes first, then thumbs — the same order as `props.images` (the
// parent puts the cover images at the front), so a cell's position in
// the list doubles as its lightbox index. Grid auto-flow handles
// placement, so cells only carry their ratio and hero flag.
const visibleCells = computed(() =>
  [...heroImages.value, ...visibleThumbs.value].map(image => ({
    url: image.url,
    aspectRatio: image.isPrimary ? BRANCH_IMAGE_GALLERY.HERO_ASPECT_RATIO : BRANCH_IMAGE_GALLERY.REST_ASPECT_RATIO,
    isHero: image.isPrimary,
  }))
)

const gridStyle = computed(() => {
  const heroCount = heroImages.value.length
  const thumbCount = visibleThumbs.value.length

  // No hero: a single row of equal squares spanning the width.
  if (heroCount === 0) {
    return {
      gridTemplateColumns: buildEqualColumns(Math.max(thumbCount, 1)),
      gridTemplateRows: 'auto',
      gridAutoFlow: 'row',
    }
  }

  // Hero(s) on the left (each spans both rows via `.gallery-cell--hero`),
  // then thumbs packed two-per-column to their right by column auto-flow.
  const thumbColumnCount = Math.ceil(thumbCount / 2)
  const columns = [...repeatTrack(heroCount, HERO_COLUMN_WIDTH), ...repeatTrack(thumbColumnCount, THUMB_COLUMN_WIDTH)]

  return {
    gridTemplateColumns: columns.join(' '),
    gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
    gridAutoFlow: 'column',
  }
})

function repeatTrack(count: number, width: string): string[] {
  return Array.from({ length: count }, () => width)
}

// `count` equal columns sharing the row width minus the gaps between
// them, capped at the same max width as a thumbnail.
function buildEqualColumns(count: number): string {
  const columnWidth = `min(calc((100% - ${(count - 1) * COL_GAP_PX}px) / ${count}), ${THUMB_MAX_WIDTH_PX}px)`

  return repeatTrack(count, columnWidth).join(' ')
}
</script>

<style lang="scss" scoped>
// Hero tiles span both grid rows; column auto-flow then drops the
// thumbnails into the remaining cells two-per-column.
.gallery-cell--hero {
  grid-row: span 2;
}

.view-more-btn {
  box-shadow: 0 0 7px 0 rgba(0, 0, 0, 0.1);
}
</style>
